import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Copy, RefreshCw, Download, Send } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { api } from "../lib/api";

const WEBHOOK_BASE = `${process.env.REACT_APP_BACKEND_URL}/api/webhooks`;

export default function SettingsDialog({ open, onOpenChange }) {
  const [bookingUrl, setBookingUrl] = useState("");
  const [status, setStatus] = useState({});
  const [syncing, setSyncing] = useState(false);
  const [migrating, setMigrating] = useState(false);
  const [reconciling, setReconciling] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [slackTesting, setSlackTesting] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get("/settings").then(({ data }) => setBookingUrl(data.calendly_booking_url || ""));
    api.get("/integrations/status").then(({ data }) => setStatus(data));
  }, [open]);

  const save = async () => {
    await api.put("/settings", { calendly_booking_url: bookingUrl });
    toast.success("Settings saved");
  };

  const copy = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied`);
  };

  const runSync = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/calendly/sync");
      toast.success(`Calendly sync complete — ${data.matched} matched, ${data.created} new`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sync failed");
    } finally {
      setSyncing(false);
    }
  };

  const runMigration = async () => {
    setMigrating(true);
    try {
      const { data } = await api.post("/prospects/migrate-stages");
      toast.success(data.migrated > 0 ? `Backfill complete — ${data.migrated} legacy leads moved into the new follow-up flow` : "Nothing to migrate — all leads already on the new stages");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Migration failed");
    } finally {
      setMigrating(false);
    }
  };

  const runReconcile = async () => {
    setReconciling(true);
    try {
      const { data } = await api.post("/zoom/reconcile");
      toast.success(`Zoom reconcile done — scanned ${data.scanned} recent recording${data.scanned === 1 ? "" : "s"}. Matched calls are attached to their prospects.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Zoom reconcile failed");
    } finally {
      setReconciling(false);
    }
  };

  const exportLeads = async () => {
    setExporting(true);
    try {
      const res = await api.get("/prospects/export", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `pit-leads-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Leads exported — check your downloads");
    } catch {
      toast.error("Could not export leads");
    } finally {
      setExporting(false);
    }
  };

  const testSlack = async () => {
    setSlackTesting(true);
    try {
      await api.post("/slack/test");
      toast.success("Test message sent — check your Slack channel");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Slack test failed");
    } finally {
      setSlackTesting(false);
    }
  };

  const Row = ({ label, value, testId }) => (
    <div className="flex items-center gap-2 mt-1">
      <code className="flex-1 text-[11px] font-mono-tech text-[#9cc6f0] bg-black/30 rounded-md px-2 py-1.5 truncate">{value}</code>
      <button data-testid={testId} onClick={() => copy(value, label)} className="p-1.5 rounded-md text-slate-400 hover:text-white hover:bg-white/10">
        <Copy size={13} />
      </button>
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="glass-panel border-white/10 max-w-lg max-h-[85vh] overflow-y-auto" data-testid="settings-dialog">
        <DialogHeader>
          <DialogTitle className="text-white">Integrations &amp; Settings</DialogTitle>
          <DialogDescription className="text-slate-400 text-sm">
            Connect Typeform and Calendly to auto-populate your pipeline.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-5 text-sm">
          <div className="flex items-center justify-between rounded-lg bg-[#4690d9]/[0.07] border border-[#4690d9]/25 p-3">
            <div>
              <p className="text-white font-semibold">Export all leads (CSV)</p>
              <p className="text-[11px] text-slate-500 max-w-[17rem]">
                Every lead with source, stage, dates and deal value. For marketing to cross-reference cost per lead.
              </p>
            </div>
            <button
              data-testid="export-leads-btn"
              onClick={exportLeads}
              disabled={exporting}
              className="flex items-center gap-2 px-4 py-2 rounded-full bg-[#4690d9] hover:bg-[#5a9fe0] text-white text-sm font-semibold disabled:opacity-50 transition-colors"
            >
              {exporting ? <RefreshCw size={14} className="animate-spin" /> : <Download size={14} />} {exporting ? "Exporting…" : "Export CSV"}
            </button>
          </div>
          <div className="flex items-center justify-between rounded-lg bg-white/[0.03] border border-white/10 p-3">
            <div>
              <p className="text-white font-semibold">Slack new-lead alerts {status.slack_configured ? "· connected" : "· not set up"}</p>
              <p className="text-[11px] text-slate-500 max-w-[17rem]">
                Posts every new lead to your Slack channel with their details and a link to PIT.
              </p>
            </div>
            <button
              data-testid="slack-test-btn"
              onClick={testSlack}
              disabled={slackTesting || !status.slack_configured}
              className="flex items-center gap-2 px-4 py-2 rounded-full border border-[#4690d9]/40 text-[#9cc6f0] hover:bg-[#4690d9]/10 text-sm font-semibold disabled:opacity-50 transition-colors"
              title={status.slack_configured ? "Send a test alert to Slack" : "Add your Slack webhook first"}
            >
              <Send size={14} /> {slackTesting ? "Sending…" : "Send test"}
            </button>
          </div>
          <div>
            <p className="eyebrow text-[10px] mb-1">Calendly booking link (used in outreach templates)</p>
            <div className="flex gap-2">
              <input
                data-testid="booking-url-input"
                value={bookingUrl}
                onChange={(e) => setBookingUrl(e.target.value)}
                placeholder="https://calendly.com/your-team/strategy-call"
                className="flex-1 bg-black/30 border border-white/10 rounded-md px-3 py-2 text-white placeholder:text-slate-600 focus:outline-none focus:border-[#4690d9]"
              />
              <button data-testid="save-settings-btn" onClick={save} className="px-4 py-2 rounded-md bg-[#4690d9] hover:bg-[#5a9fe0] text-white font-semibold">
                Save
              </button>
            </div>
          </div>
          <div>
            <p className="eyebrow text-[10px]">
              Typeform webhook URL {status.typeform_secret_set ? "· secret verified" : "· no secret set"}
            </p>
            <Row label="Typeform webhook URL" value={`${WEBHOOK_BASE}/typeform`} testId="copy-typeform-webhook-btn" />
            <p className="text-[11px] text-slate-500 mt-1">Paste in Typeform → your form → Connect → Webhooks.</p>
          </div>
          <div>
            <p className="eyebrow text-[10px]">
              Calendly webhook URL {status.calendly_signing_key_set ? "· signature verified" : "· no signing key set"}
            </p>
            <Row label="Calendly webhook URL" value={`${WEBHOOK_BASE}/calendly`} testId="copy-calendly-webhook-btn" />
            <p className="text-[11px] text-slate-500 mt-1">Registered via Calendly API webhook subscription (invitee.created + invitee.canceled).</p>
          </div>
          <div className="flex items-center justify-between border-t border-white/[0.08] pt-4">
            <div>
              <p className="text-white font-semibold">Calendly sync</p>
              <p className="text-[11px] text-slate-500">
                {status.calendly_api_key_set ? "Pull bookings from the last 90 days" : "Add CALENDLY_API_KEY to enable"}
              </p>
            </div>
            <button
              data-testid="calendly-sync-btn"
              onClick={runSync}
              disabled={syncing}
              className="flex items-center gap-2 px-4 py-2 rounded-full bg-[#4690d9] hover:bg-[#5a9fe0] text-white text-sm font-semibold disabled:opacity-50 transition-colors"
            >
              <RefreshCw size={14} className={syncing ? "animate-spin" : ""} /> Sync now
            </button>
          </div>
          <div className="flex items-center justify-between border-t border-white/[0.08] pt-4">
            <div>
              <p className="text-white font-semibold">Backfill legacy stages</p>
              <p className="text-[11px] text-slate-500 max-w-[16rem]">
                One-time fix. Moves old Contacted / Call Completed / Follow Up leads into the new Book-Call &amp; Close follow-up flows. Safe to run more than once.
              </p>
            </div>
            <button
              data-testid="migrate-stages-btn"
              onClick={runMigration}
              disabled={migrating}
              className="flex items-center gap-2 px-4 py-2 rounded-full border border-amber-500/40 text-amber-300 hover:bg-amber-500/10 text-sm font-semibold disabled:opacity-50 transition-colors"
            >
              <RefreshCw size={14} className={migrating ? "animate-spin" : ""} /> Run backfill
            </button>
          </div>
          <div className="flex items-center justify-between border-t border-white/[0.08] pt-4">
            <div>
              <p className="text-white font-semibold">Reconcile Zoom recordings</p>
              <p className="text-[11px] text-slate-500 max-w-[16rem]">
                Pulls the last 3 days of Zoom cloud recordings and attaches transcripts to matching prospects. Use if a call didn&apos;t auto-import.
              </p>
            </div>
            <button
              data-testid="zoom-reconcile-btn"
              onClick={runReconcile}
              disabled={reconciling}
              className="flex items-center gap-2 px-4 py-2 rounded-full border border-[#4690d9]/40 text-[#9cc6f0] hover:bg-[#4690d9]/10 text-sm font-semibold disabled:opacity-50 transition-colors"
            >
              <RefreshCw size={14} className={reconciling ? "animate-spin" : ""} /> Reconcile now
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
